/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import useGetBillingCloudResources from '@modules/billing-lib/queries/useGetBillingCloudResources'
import { connectedClusterURL } from '@modules/ccm-lib/urls'
import { projectUrl } from '@modules/project-user-lib/urls'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

import { deploymentUrl } from '@/lib/urlBuilder'

import type { Budget } from '../../../types'

type Props = {
  scopeValues: Budget['scope_values']
}

const useCloudResourceDetails = ({ scopeValues }: Props) => {
  const organizationId = useOrganizationId()

  const {
    data: billingCloudResources,
    isLoading,
    isError,
  } = useGetBillingCloudResources(organizationId)

  const scopeValue = scopeValues[0]

  const matchedResource = billingCloudResources?.find((resource) => resource.id === scopeValue)

  const name = matchedResource?.name || scopeValue
  const id = scopeValue
  let cloudResourceLink: string | undefined

  switch (matchedResource?.type) {
    case 'cloud_connect':
      cloudResourceLink = connectedClusterURL(matchedResource.id)
      break
    case 'deployment':
      cloudResourceLink = deploymentUrl(matchedResource.id)
      break
    case 'elasticsearch':
    case 'observability':
    case 'security':
      cloudResourceLink = projectUrl(matchedResource.type, matchedResource.id)
      break
    default:
  }

  return {
    cloudResourceLink,
    isError,
    id,
    isLoading,
    name,
  }
}

export default useCloudResourceDetails
